import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Check, CreditCard, HardDrive, Landmark, Minus, Plus, Sparkles, Zap } from 'lucide-react';
import { PLANS, RENDER_PACK_MINUTES, STORAGE_ADDON_GB, TRIAL_DAYS, isPaidPlan, type BillingInterval, type PaidPlanId, type PlanSpec } from '@vidmakr/shared';
import { CompanyDetailsCard, EnterpriseDialog, InvoicesCard, useBillingDetails } from '@/components/BillingDetails';
import { Badge, Button, Card, Modal, PageHeader, Progress, Spinner, cx } from '@/components/ui';
import { formatBytes } from '@/lib/format';
import { api } from '@/lib/api';
import { errorMessage, toast } from '@/lib/toast';
import { useOrg } from '@/lib/hooks';
import { formatMinutes, formatMoney, goToStripe, useBillingInfo, useOrgPlan } from '@/lib/billing';

const GB = 1024 ** 3;

/** What a plan card lists. */
function features(p: PlanSpec): string[] {
  const per = (n: number, extra: number, unit = '') => (extra ? `${n.toLocaleString()}${unit} + ${extra.toLocaleString()}${unit} per extra editor` : `${n.toLocaleString()}${unit}`);
  const gb = (n: number) => (n >= 1000 && n % 1000 === 0 ? `${n / 1000} TB` : `${n} GB`);
  const res = p.maxExportShortSide >= 4320 ? 'Exports up to 8K' : p.maxExportShortSide >= 2160 ? 'Exports up to 4K' : `Exports up to ${p.maxExportShortSide}p, with a small watermark`;
  const out = [
    `${gb(p.storageGb)} storage${p.storagePerExtraSeatGb ? ` + ${gb(p.storagePerExtraSeatGb)} per extra editor` : ''}`,
    `${per(p.renderMinutes, p.renderMinutesPerExtraSeat)} render minutes a month`,
    `${per(p.aiMinutes, p.aiMinutesPerExtraSeat)} transcription & voiceover minutes a month`,
    res,
    p.reviewPro ? 'Review links with versions, drawings and approvals' : `Review links with comments (${p.reviewLinkMaxDays} days)`,
    Number.isFinite(p.maxViewers) ? `${p.maxViewers} viewers` : 'Unlimited viewers and reviewers',
    p.exportRetentionDays >= 730 ? 'Exports kept two years' : p.exportRetentionDays >= 365 ? 'Exports kept a year' : `Exports kept ${p.exportRetentionDays} days`,
  ];
  if (Number.isFinite(p.maxProjects)) out.push(`${p.maxProjects} projects`);
  if (p.mcp) out.push('AI agents (MCP)');
  if (p.masterFormats) out.push('ProRes and lossless');
  if (p.accurateTranscripts) out.push('High-accuracy transcripts');
  if (p.gpu) out.push('GPU exports and your own GPU workers');
  if (p.priority) out.push('Priority render queue');
  if (p.reviewBranding) out.push('Your logo on review pages');
  if (p.invoicing) out.push('Pay by invoice on payment terms', 'Your company details and VAT number on invoices');
  return out;
}

function Meter({ label, used, limit, format, hint }: { label: string; used: number; limit: number; format: (n: number) => string; hint?: string }) {
  const unlimited = !Number.isFinite(limit);
  const ratio = unlimited ? 0 : used / Math.max(1e-9, limit);
  return (
    <div data-meter={label}>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className={cx('tabular-nums', ratio > 1 ? 'text-danger' : ratio > 0.85 ? 'text-warn' : 'text-muted')}>
          {format(used)} {unlimited ? '' : `of ${format(limit)}`}
        </span>
      </div>
      <Progress value={Math.min(1, ratio)} />
      {hint ? <p className="mt-1 text-[11px] text-faint">{hint}</p> : null}
    </div>
  );
}

function Stepper({ value, min, max, onChange, label }: { value: number; min: number; max: number; onChange: (n: number) => void; label: string }) {
  // The number can also be typed; it's applied (clamped to min–max) on Enter or when the field loses focus,
  // so a half-typed "1" on the way to "15" never reaches onChange.
  const [draft, setDraft] = useState<string | null>(null);
  const cancelled = useRef(false);
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      setDraft(null);
      return;
    }
    if (draft === null) return;
    const n = Number.parseInt(draft, 10);
    setDraft(null);
    if (Number.isFinite(n)) {
      const v = Math.min(max, Math.max(min, n));
      if (v !== value) onChange(v);
    }
  };
  return (
    <div className="flex items-center gap-1" aria-label={label}>
      <Button size="icon-sm" variant="secondary" disabled={value <= min} onClick={() => onChange(value - 1)} aria-label={`Fewer ${label}`}>
        <Minus size={13} />
      </Button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={`Number of ${label}`}
        className="h-7 w-11 rounded-md border border-transparent bg-transparent text-center text-sm tabular-nums text-fg hover:border-line focus:border-accent focus:bg-panel-2 focus:outline-none"
        value={draft ?? String(value)}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
        onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            cancelled.current = true;
            e.currentTarget.blur();
          }
        }}
        data-stepper-value
      />
      <Button size="icon-sm" variant="secondary" disabled={value >= max} onClick={() => onChange(value + 1)} aria-label={`More ${label}`}>
        <Plus size={13} />
      </Button>
    </div>
  );
}

const dateOf = (t: { microsSinceUnixEpoch: bigint }) => new Date(Number(t.microsSinceUnixEpoch / 1000n)).toLocaleDateString();

interface ChangePreview {
  prorationDate: number;
  currency: string;
  interval: BillingInterval;
  /** Charged now; null when Stripe couldn't preview it. */
  dueNow: number | null;
  credit: number;
  recurring: number;
}

function ChangeDialog({
  pending,
  byInvoice,
  invoiceDays,
  busy,
  onClose,
  onConfirm,
}: {
  pending: { preview?: ChangePreview | null } | null;
  byInvoice: boolean;
  invoiceDays: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const p = pending?.preview;
  const money = (n: number) => formatMoney(n, p?.currency);
  const how = byInvoice ? `invoiced now, due in ${invoiceDays} days` : 'charged to your card now';
  return (
    <Modal
      open={!!pending}
      onClose={onClose}
      title="Confirm the change"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!p} onClick={onConfirm} data-confirm-change>
            {p?.dueNow ? `Pay ${money(p.dueNow)} and update` : 'Update subscription'}
          </Button>
        </>
      }
    >
      {!p ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={14} /> Working out the price…
        </div>
      ) : (
        <div className="space-y-2 text-sm" data-change-preview>
          {p.dueNow === null ? (
            <p>The difference for the rest of this billing period is {how}.</p>
          ) : p.dueNow > 0 ? (
            <p>
              <b className="text-fg">{money(p.dueNow)}</b> for the rest of this billing period, {how}.
            </p>
          ) : p.credit > 0 ? (
            <p>
              <b className="text-fg">{money(p.credit)}</b> for the unused part of this billing period goes to your account as credit, taken off your
              next invoices.
            </p>
          ) : (
            <p>Nothing to pay now.</p>
          )}
          <p className="text-muted">
            Then {money(p.recurring)} per {p.interval === 'year' ? 'year' : 'month'}.
          </p>
        </div>
      )}
    </Modal>
  );
}

export function BillingPage() {
  const { orgId } = useParams();
  const [params, setParams] = useSearchParams();
  const { role } = useOrg(orgId);
  const info = useBillingInfo();
  const plan = useOrgPlan(orgId);
  const [interval, setInterval] = useState<BillingInterval>('year');
  const [seats, setSeats] = useState<Record<PaidPlanId, number>>({ pro: 1, team: 3, enterprise: 10 });
  const [busy, setBusy] = useState('');
  const owner = role === 'owner';
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const details = useBillingDetails(orgId, owner && !!info?.checkout);

  useEffect(() => {
    if (params.get('checkout') === 'success') toast.success('Thanks! Your subscription is active.');
    if (params.get('pack') === 'success') toast.success('Render minutes added for this month.');
    if (params.get('checkout') || params.get('pack')) setParams({}, { replace: true });
  }, [params, setParams]);

  const org = plan?.org;
  const current = plan?.limits.plan.id;
  const subscribed = !!org?.stripeSubscriptionId && ['active', 'trialing', 'past_due'].includes(org.billingStatus);
  // A subscription set to end (in Stripe, the customer portal or by a cancellation request).
  const cancelsOn = subscribed && org?.cancelAt ? dateOf(org.cancelAt) : '';
  // A paid plan without a subscription behind it was set by a superadmin (Admin → Organizations → Set plan).
  const manual = !!current && isPaidPlan(current) && !subscribed;
  // Start the seat pickers at what the organization has now.
  useEffect(() => {
    if (!plan) return;
    const editors = Math.max(1, plan.editors);
    setSeats({
      pro: Math.min(5, Math.max(subscribed && current === 'pro' ? plan.limits.seats : editors, 1)),
      team: Math.max(3, subscribed && current === 'team' ? plan.limits.seats : editors),
      enterprise: Math.max(10, subscribed && current === 'enterprise' ? plan.limits.seats : editors),
    });
    if (org?.billingInterval === 'month' || org?.billingInterval === 'year') setInterval(org.billingInterval);
  }, [plan?.org.id, subscribed, current]); // eslint-disable-line react-hooks/exhaustive-deps

  const storageUnits = Math.round((org?.extraStorageGb ?? 0) / STORAGE_ADDON_GB);
  const prices = info?.prices;
  const money = (n: number) => formatMoney(n, prices?.currency);
  const apply = async (body: Record<string, unknown>, label: string) => {
    setBusy(label);
    try {
      await api('/api/billing/change', { method: 'POST', json: { orgId, ...body } });
      toast.success('Subscription updated');
      void details.reload();
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy('');
    }
  };
  // Changes to what's billed are confirmed first, with what's charged now; switching how it's paid isn't.
  const [pending, setPending] = useState<{ body: Record<string, unknown>; label: string; preview?: ChangePreview | null } | null>(null);
  const change = async (body: Record<string, unknown>, label: string) => {
    if (Object.keys(body).every((k) => k === 'payByInvoice')) return void apply(body, label);
    setPending({ body, label });
    try {
      const preview = await api<ChangePreview>('/api/billing/change/preview', { method: 'POST', json: { orgId, ...body } });
      setPending((p) => (p && p.body === body ? { ...p, preview } : p));
    } catch (e) {
      // The preview refuses changes the update would refuse too (e.g. fewer seats than editors).
      setPending(null);
      toast.error(errorMessage(e));
    }
  };

  const yearlySaving = useMemo(() => {
    if (!prices) return 0;
    return Math.round((1 - prices.pro.year / prices.pro.month) * 100);
  }, [prices]);

  if (!plan || !org || !info) return null;
  const l = plan.limits;

  if (!plan.enabled) {
    return (
      <>
        <PageHeader title="Plan & billing" subtitle={org.name} />
        <Card className="max-w-3xl p-5 text-sm text-muted">
          Billing is not enabled on this installation: every feature is available, with {formatBytes(l.storageBytes)} of storage per organization.
        </Card>
      </>
    );
  }

  const planCard = (p: PlanSpec) => {
    const paid = isPaidPlan(p.id);
    const isCurrent = current === p.id || (p.id === 'pro' && current === 'trial');
    const price = paid ? prices![p.id as PaidPlanId][interval] : 0;
    const n = paid ? seats[p.id as PaidPlanId] : 1;
    const action = () => {
      if (!paid) return;
      const body = { plan: p.id, interval, seats: n };
      if (subscribed) void change(body, p.id);
      // Enterprise asks for company details and how to pay first.
      else if (p.invoicing) setEnterpriseOpen(true);
      else void goToStripe('/api/billing/checkout', { orgId, ...body });
    };
    const label = !paid
      ? current === 'free'
        ? 'Current plan'
        : 'Included'
      : subscribed
        ? current === p.id
          ? n !== l.seats || interval !== org.billingInterval
            ? 'Update subscription'
            : 'Current plan'
          : `Switch to ${p.label}`
        : current === 'trial' && p.id === 'pro'
          ? 'Keep Pro'
          : `Choose ${p.label}`;
    const disabled = !owner || !paid || label === 'Current plan' || !info.checkout || !!busy;
    return (
      <Card key={p.id} className={cx('flex flex-col p-5', isCurrent && 'border-accent/60')} data-plan-card={p.id}>
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-base font-semibold">{p.label}</h3>
          {isCurrent ? <Badge tone="accent">{current === 'trial' && p.id === 'pro' ? 'Trial' : 'Current'}</Badge> : null}
        </div>
        <div className="mb-3">
          {paid ? (
            <>
              <span className="text-2xl font-bold">{money(price)}</span>
              <span className="text-sm text-muted"> / editor / month</span>
              {interval === 'year' ? <div className="text-[11px] text-faint">billed yearly</div> : null}
            </>
          ) : (
            <span className="text-2xl font-bold">{money(0)}</span>
          )}
        </div>
        <ul className="mb-4 flex-1 space-y-1.5 text-[13px] text-muted">
          {features(p).map((f) => (
            <li key={f} className="flex gap-1.5">
              <Check size={14} className="mt-0.5 shrink-0 text-ok" /> {f}
            </li>
          ))}
        </ul>
        {paid ? (
          <div className="mb-3 flex items-center justify-between gap-2 text-sm">
            <span className="text-muted">Editor seats</span>
            <Stepper label="seats" value={n} min={Math.max(p.minSeats, plan.editors)} max={p.maxSeats} onChange={(v) => setSeats((s) => ({ ...s, [p.id]: v }))} />
          </div>
        ) : null}
        {paid ? (
          // Label and monthly total on separate lines so long ones ("Choose Enterprise · 4 900 USD/mo") fit narrow
          // cards, with one height for every card's button.
          <Button
            variant={isCurrent ? 'secondary' : 'primary'}
            disabled={disabled}
            loading={busy === p.id}
            onClick={action}
            data-plan-action={p.id}
            className={cx('h-auto! min-h-[50px] w-full flex-col gap-0! py-1.5 text-center whitespace-normal!', !isCurrent && 'border border-transparent')}
          >
            <span>{label}</span>
            {label !== 'Current plan' ? <span className="text-xs font-normal opacity-80">{money(price * n)}/mo</span> : null}
          </Button>
        ) : (
          <Button variant="ghost" disabled>
            {label}
          </Button>
        )}
      </Card>
    );
  };

  return (
    <>
      <PageHeader
        title="Plan & billing"
        subtitle={org.name}
        actions={
          owner && org.stripeCustomerId ? (
            <Button variant="secondary" onClick={() => void goToStripe('/api/billing/portal', { orgId })}>
              <CreditCard size={14} /> Invoices & payment
            </Button>
          ) : null
        }
      />
      <div className="grid max-w-5xl gap-6">
        <Card className="p-5" data-current-plan={current}>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <h2 className="font-semibold">{l.plan.label}</h2>
            {plan.trialDaysLeft !== null ? (
              <Badge tone="warn">
                {plan.trialDaysLeft} day{plan.trialDaysLeft === 1 ? '' : 's'} left
              </Badge>
            ) : null}
            {subscribed ? (
              <span className="text-sm text-muted">
                {l.seats} editor seat{l.seats === 1 ? '' : 's'} · billed {org.billingInterval === 'year' ? 'yearly' : 'monthly'}
                {org.currentPeriodEnd && !cancelsOn ? ` · renews ${dateOf(org.currentPeriodEnd)}` : ''}
              </span>
            ) : null}
            {cancelsOn ? (
              <span data-cancels-on>
                <Badge tone="warn">Cancels {cancelsOn}</Badge>
              </span>
            ) : null}
            {manual ? (
              <span data-manual-plan>
                <Badge tone="accent">Set by vidmakr</Badge>
              </span>
            ) : null}
            {subscribed && details.state?.payByInvoice ? <Badge tone="neutral">Paid by invoice</Badge> : null}
            {org.billingStatus === 'past_due' ? <Badge tone="danger">{details.state?.payByInvoice ? 'Invoice overdue' : 'Payment failed — update your card'}</Badge> : null}
          </div>
          {cancelsOn ? (
            <p className="mb-4 rounded-lg border border-warn/40 bg-warn/10 p-3 text-sm">
              This subscription has been cancelled and ends on <b>{cancelsOn}</b>. Until then everything works as now; afterwards the workspace moves to
              Free and nothing is deleted.{' '}
              {owner ? 'To keep it, renew it under Invoices & payment.' : 'An owner can renew it before then.'}
            </p>
          ) : null}
          {manual ? (
            <p className="mb-4 text-sm text-muted">
              A vidmakr administrator has put this workspace on {l.plan.label}
              {plan.trialDaysLeft === null ? '' : ' for a limited time'}, so there's nothing to pay for it. Choosing a plan below starts a paid subscription
              instead.
            </p>
          ) : null}
          {current === 'trial' ? (
            <p className="mb-4 text-sm text-muted">
              You're trying Pro for {TRIAL_DAYS} days with smaller allowances. Choose a plan to keep its features; nothing is deleted if you don't, and the workspace moves to Free.
            </p>
          ) : null}
          {org.overLimitSince ? (
            <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm">
              Storage is over the limit: uploads and new exports are paused. Delete old exports or media, or add storage.
            </p>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Meter label="Storage" used={org.storageUsedBytes} limit={l.storageBytes} format={(n) => formatBytes(n)} hint="Originals, editing proxies and exports." />
            <Meter
              label="Render minutes this month"
              used={plan.usage.renderMinutes}
              limit={plan.renderAllowance}
              format={formatMinutes}
              hint={`1080p minute = 1, 4K = 4, 720p = ½; ProRes/lossless count double.${plan.usage.bonusRenderMinutes ? ` Includes ${formatMinutes(plan.usage.bonusRenderMinutes)} from packs.` : ''}`}
            />
            <Meter label="Transcription & voiceover minutes" used={plan.usage.aiMinutes} limit={l.aiMinutes} format={formatMinutes} />
            <Meter label="Editor seats" used={plan.editors} limit={l.maxEditors} format={(n) => String(n)} hint={`Owners, admins and editors. Viewers: ${plan.viewers}${Number.isFinite(l.maxViewers) ? ` of ${l.maxViewers}` : ''}.`} />
          </div>
          <p className="mt-3 text-[11px] text-faint">Monthly allowances start over on the 1st (UTC).</p>
        </Card>

        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Plans</h2>
            <div className="flex rounded-lg bg-panel-2 p-1 text-xs" role="radiogroup" aria-label="Billing period">
              {(['month', 'year'] as const).map((i) => (
                <button
                  key={i}
                  role="radio"
                  aria-checked={interval === i}
                  className={cx('rounded-md px-3 py-1', interval === i ? 'bg-panel-3 text-fg' : 'text-muted hover:text-fg')}
                  onClick={() => setInterval(i)}
                >
                  {i === 'month' ? 'Monthly' : `Yearly${yearlySaving > 0 ? ` · save ${yearlySaving}%` : ''}`}
                </button>
              ))}
            </div>
          </div>
          {!owner ? <p className="mb-3 text-sm text-muted">Only owners can change the plan.</p> : null}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">{[PLANS.free, PLANS.pro, PLANS.team, PLANS.enterprise].map(planCard)}</div>
        </div>

        {subscribed && owner && current === 'enterprise' && details.state?.invoicing.allowed ? (
          <Card className="flex flex-wrap items-center justify-between gap-3 p-5" data-payment-method>
            <div>
              <div className="mb-1 flex items-center gap-2">
                <Landmark size={15} className="text-muted" />
                <h3 className="font-semibold">How you pay</h3>
              </div>
              <p className="text-sm text-muted">
                {details.state.payByInvoice
                  ? `By invoice, due ${details.state.invoicing.days} days after each one.`
                  : 'By card. You can pay by invoice instead.'}
              </p>
            </div>
            <Button
              loading={busy === 'collection'}
              disabled={!!busy}
              onClick={() => void change({ payByInvoice: !details.state!.payByInvoice }, 'collection')}
              data-switch-collection
            >
              {details.state.payByInvoice ? 'Pay by card instead' : 'Pay by invoice instead'}
            </Button>
          </Card>
        ) : null}

        {owner && info.checkout ? (
          <>
            <CompanyDetailsCard orgId={org.id} details={details} />
            {org.stripeCustomerId ? <InvoicesCard orgId={org.id} refreshKey={`${org.stripeSubscriptionId}:${org.seats}:${org.plan}`} /> : null}
          </>
        ) : null}
        <ChangeDialog
          pending={pending}
          byInvoice={!!details.state?.payByInvoice}
          invoiceDays={details.state?.invoicing.days ?? 0}
          busy={!!pending && busy === pending.label}
          onClose={() => setPending(null)}
          onConfirm={async () => {
            if (!pending) return;
            if (await apply({ ...pending.body, prorationDate: pending.preview?.prorationDate }, pending.label)) setPending(null);
          }}
        />
        {enterpriseOpen ? (
          <EnterpriseDialog
            open
            onClose={() => setEnterpriseOpen(false)}
            orgId={org.id}
            details={details}
            interval={interval}
            seats={seats.enterprise}
            pricePerSeat={prices!.enterprise[interval]}
            currency={prices!.currency}
            onDone={() => void details.reload()}
          />
        ) : null}

        {subscribed ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-5">
              <div className="mb-1 flex items-center gap-2">
                <HardDrive size={15} className="text-muted" />
                <h3 className="font-semibold">Extra storage</h3>
              </div>
              <p className="mb-4 text-sm text-muted">
                {STORAGE_ADDON_GB / 1000} TB for {money(prices!.storage)} / month each; the rest of this billing period is charged when you add it.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm">{storageUnits ? `${storageUnits} × ${STORAGE_ADDON_GB / 1000} TB` : 'None'}</span>
                <Stepper
                  label="storage add-ons"
                  value={storageUnits}
                  min={0}
                  max={100}
                  onChange={(v) => owner && void change({ storageUnits: v }, 'storage')}
                />
              </div>
            </Card>
            <Card className="p-5">
              <div className="mb-1 flex items-center gap-2">
                <Zap size={15} className="text-muted" />
                <h3 className="font-semibold">Render pack</h3>
              </div>
              <p className="mb-4 text-sm text-muted">
                {RENDER_PACK_MINUTES} extra render minutes for this month, {money(prices!.renderPack)} once.
              </p>
              <Button disabled={!owner || !info.checkout} onClick={() => void goToStripe('/api/billing/render-pack', { orgId, packs: 1 })} data-buy-render-pack>
                <Sparkles size={14} /> Buy {RENDER_PACK_MINUTES} minutes
              </Button>
            </Card>
          </div>
        ) : null}

        <p className="text-xs text-faint">
          Viewers and people you share review links with are always free. Storage counts {formatBytes(GB)} as 1 GB. Prices exclude tax where it applies.
        </p>
      </div>
    </>
  );
}
